import { Illustration, Pill, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useIsJobsEnabled } from "@src/appV2/redesign/PlacementCandidate/useIsJobsEnabled";

interface RestrictedAccountContentProps {
  readonly isFinalRestriction: boolean;
  readonly liftTime?: string;
  readonly reEnrollmentScore: number;
  readonly onContinue: () => void;
  readonly enableViewAnalytics?: boolean;
}

export function RestrictedAccountContent(props: RestrictedAccountContentProps) {
  const { isFinalRestriction, liftTime, reEnrollmentScore, onContinue, enableViewAnalytics } =
    props;
  const isJobsEnabled = useIsJobsEnabled();

  useLogEffect(
    APP_V2_USER_EVENTS.CLIPBOARD_SCORE_RESTRICTED_BOTTOM_SHEET_VIEWED,
    {},
    {
      enabled: enableViewAnalytics,
    }
  );

  const handleContinueClick = (): void => {
    logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_RESTRICTED_BOTTOM_SHEET_CONTINUE_CLICKED, {});
    onContinue();
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={10}
          sx={{
            padding: "8px",
            paddingTop: "10px",
            textAlign: "center",
            minHeight: "calc(100vh - 250px)",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Illustration type="circled-cross-with-exclamation" />

          <Text variant="h3" textAlign="center">
            Your account is restricted
          </Text>

          <Stack spacing={5}>
            {isFinalRestriction ? (
              <>
                <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                  You&apos;ve been removed from your shifts, and you can&apos;t book open shifts at
                  this time. When your Restriction lifts, your score will reset to{" "}
                  {reEnrollmentScore}.
                </Text>
                <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                  Since this is the 3rd time in the past 6 months, this Restriction lasts for 1
                  year.
                </Text>
              </>
            ) : (
              <>
                <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                  Your Clipboard score reached 0
                </Text>
                <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                  You&apos;ve been removed from your future shifts, and you can&apos;t book new
                  shifts at this time. When your Restriction lifts, your score will reset to{" "}
                  {reEnrollmentScore}.
                </Text>
              </>
            )}

            {isJobsEnabled && (
              <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                This restriction does not impact your access to Jobs on Clipboard: you can still
                apply and interview for permanent positions with workplaces.
              </Text>
            )}
          </Stack>

          {liftTime && (
            <Pill
              iconType="clock-filled"
              label={`Restriction lifts on ${liftTime}`}
              size="small"
              variant="outlined"
              sx={{
                display: "inline-flex",
                backgroundColor: `rgba(255, 255, 255, 0.7)`,
                padding: 5,
                gap: 3,
              }}
            />
          )}
        </Stack>
      </Box>
      <DialogFooter
        orientation="horizontal"
        sx={{
          background: "transparent",
        }}
      >
        <Stack spacing={5} sx={{ width: "100%", alignItems: "center" }}>
          <TextButton
            underline
            sx={{
              fontWeight: "bold",
              marginBottom: "10px !important",
              textAlign: "center",
            }}
            onClick={() => {
              void openZendeskMessaging();
              onContinue();
            }}
          >
            Tap if you think this is a mistake, or if you&apos;d like to speak with a
            representative.
          </TextButton>
          <Button fullWidth variant="contained" size="large" onClick={handleContinueClick}>
            Got it
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
}
